import { Component, OnInit, Input, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { PoEmailData } from '../../../../../models/poData/FormattedContacts';
import { EmailStatusChangeData } from '../../../../../models/email/EmailStatusChangeData.interface';
import { ContactBadge, contactInsightBadge } from '../../../../../domain/receiving-log/rules/contact-insight.rules';
import { looksLikeEmailAddress } from '../../../../../domain/receiving-log/rules/generate-email';

/**
 * Contact List Item Component
 * ---------------------------
 * This component represents a single contact item within the Add Contact modal. It provides logic to handle the display 
 * and interaction with contact information, including email validation and primary contact selection.
 *
 * Features:
 * - Handles the display of contact information with validation for email addresses.
 * - Emits events to toggle the inclusion of the contact in the notify list.
 * - Emits events to set the contact as the primary contact.
 *
 * Component Interactions:
 * - Users can toggle the contact's inclusion in the notify list via the checkbox.
 * - Clicking the star icon sets the contact as the primary contact.
 * - Invalid email addresses are indicated with a warning icon.
 */

@Component({
  selector: 'app-contact-list-item',
  templateUrl: './contact-list-item.component.html',
  styleUrls: ['./contact-list-item.component.css'],
})
export class ContactListItemComponent {
  @Input() contact: PoEmailData | null = null;   // Input property to receive contact data
  @Input() primaryContact: string | null = null;   // Input property to receive the primary contact
  @Input() emailStatus: boolean = false;   // Input property to receive the email status

  @Output() emailStatusChange = new EventEmitter<EmailStatusChangeData>();  // Output event emitter to notify parent component of email status changes
  @Output() updatePrimaryContact = new EventEmitter<string>();   // Output event emitter to notify parent component of primary contact updates
  /** Asks the parent to search the employee directory for this contact's name. */
  @Output() findInDirectory = new EventEmitter<string>();

  /** BambooHR badge for this contact (role / group / external), or null if unknown. */
  get badge(): ContactBadge | null {
    return contactInsightBadge(this.contact?.insight);
  }

  /**
   * Whether this contact can be a recipient at all — i.e. whether it holds an
   * address rather than a name the PO supplied instead of one.
   *
   * Tests for an address on ANY domain, not `isValidEmail` (which is internal-only):
   * an off-domain vendor is a real, emailable contact. Gates both the checkbox and
   * the main-contact star, since a name can be neither notified nor printed.
   */
  get canEmail(): boolean {
    return looksLikeEmailAddress(this.contact?.contact || '');
  }

  /** Hands the contact's name to the parent's employee search. */
  onFindInDirectory(): void {
    if (this.contact) { this.findInDirectory.emit(this.contact.contact); }
  }


  /**
   * Method to handle changes in email status.
   * Emits an event to the parent component with the updated email status.
   */
  onEmailStatusChange() {
    if (this.contact) {
      const emailStatusChangeData: EmailStatusChangeData = { contact: this.contact.contact, status: this.emailStatus };
      this.emailStatusChange.emit(emailStatusChangeData);
    }
  }

  /**
   * Method to set the contact as the primary contact.
   * Emits an event to the parent component with the contact to be set as primary.
   */
  setAsPrimary() {
    if (this.contact) {
      this.updatePrimaryContact.emit(this.contact.contact);
    }
  }
}